import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import SingleTextEditor from '../common/editors/SingleTextEditor';
import AIModelSelector from '../../pages/cerokm/components/AIModelSelector';

export function useMounted() {
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return function useMountedEffectCleanup() {
      mountedRef.current = false;
    };
  }, []);
  return useCallback(() => mountedRef.current, [mountedRef]);
}

export default function AskAI({prompt, model: defaultModel = 'gpt-4o'}) {
  const {page} = useContext(LegoAdminPageContext);

  const [text, setText] = useState('');
  const [lastPrompt, setLastPrompt] = useState(prompt);
  const [activePromptId, setActivePromptId] = useState(null);
  const [model, setModel] = useState(defaultModel);
  const activePromptIdRef = useRef(null);
  const isMounted = useMounted();

  // Helper to check if model is OpenAI
  const isOpenAIModel = (model) => {
    return model && (model.startsWith('gpt-') || model.startsWith('o3') || model.startsWith('o4'));
  };

  useEffect(() => {
    tryParseGPT(lastPrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpenAIModel(model)) return; // Only listen for progress if OpenAI
    let myId = activePromptIdRef.current;
    let partialOutput = '';

    const promptProgress = ({ promptId, newOutput }) => {
      if (myId === promptId) {
        if(isMounted()) {
          partialOutput = partialOutput + newOutput;
          setText(partialOutput);
        }
      }
    };

    if(myId) {
      const gptService = page.service('services/gpt/prompt');
      gptService.on('prompt-progress', promptProgress);
    }

    return () => {
      if(myId) {
        const gptService = page.service('services/gpt/prompt');
        gptService.removeListener('prompt-progress', promptProgress);
      }
    }
  }, [activePromptId, model, isMounted, page]);

  const tryParseGPT = useCallback(async (prompt) => {
    if (isOpenAIModel(model)) {
      // Use GPT service with progress
      try {
        let promptId = Math.ceil(Math.random()*1000);
        setActivePromptId(promptId);
        activePromptIdRef.current = promptId;
        const gptService = page.service('services/gpt/prompt');
        let res = await gptService.create({ prompt, promptId, model });
        if(promptId === activePromptIdRef.current && isMounted()) {
          setText(res);
          activePromptIdRef.current = null;
          setActivePromptId(null);
        }
      } finally {
        // Progress listener cleanup handled in useEffect
      }
    } else {
      // Use OpenAIAPIService (no progress)
      setActivePromptId(null);
      activePromptIdRef.current = null;
      setText('');
      const openaiService = page.service('services/openai/prompt');
      // Compose the request as expected by OpenAIAPIService
      const res = await openaiService.create({
        userPrompt: prompt,
        options: { model },
      });
      if (isMounted()) {
        setText(res.message || (typeof res === 'string' ? res : ''));
      }
    }
  }, [model, page, isMounted]);

  // Rest of your component...

  return (
    <div>
      <textarea rows={6} className={'form-control form-control-small'} value={lastPrompt} onChange={e => setLastPrompt(e.target.value)}/>
        <div className='d-flex align-items-center'>
          <button className={'btn-primary btn mt-1'} onClick={() => tryParseGPT(lastPrompt)}>Ask AI</button>
          <span className='ml-2 w-25'>
            <AIModelSelector value={model} onChange={setModel} />
          </span>
        </div>

      <pre style={{whiteSpace: 'pre-wrap', minHeight: '30vh'}} className={'text-breakall broder rounde p-2 bg-light mt-2 small'}>
        {text?.split(/\*\*(.+?)\*\*/).map((part, i) => 
          i % 2 === 0 ? part : <strong key={i}>{part}</strong>
        )}
      </pre>
    </div>
  );
}
